(() => {
  'use strict';

  class PtFitGuide extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('.pt-fit-guide__trigger');
      if (!this.dialog || !this.trigger) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.trigger.hidden = false;
      this.trigger.addEventListener('click', () => this.open(), options);
      this.querySelector('.pt-fit-guide__close').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.restore();
      }, options);
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
        )].filter((element) => element.getClientRects().length && element.tabIndex >= 0);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (!first) {
          event.preventDefault();
          this.querySelector('h2').focus();
        } else if (event.shiftKey && (active === first || !focusable.includes(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || !focusable.includes(active))) {
          event.preventDefault();
          first.focus();
        }
      }, options);
      this.dialog.addEventListener('pointerdown', (event) => {
        this.backdropDown = this.outside(event);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (this.backdropDown && this.outside(event)) this.close();
        this.backdropDown = false;
      }, options);
    }

    outside(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right ||
        event.clientY < rect.top || event.clientY > rect.bottom;
    }

    open() {
      if (this.dialog.open) return;
      this.returnFocus = document.activeElement;
      this.scrollPosition = { x: window.scrollX, y: window.scrollY };
      const body = document.body;
      this.previousStyle = {};
      for (const key of ['position', 'top', 'left', 'right', 'width', 'overflow', 'paddingRight']) {
        this.previousStyle[key] = body.style[key];
      }
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      this.dialog.showModal();
      Object.assign(body.style, {
        position: 'fixed', top: `-${this.scrollPosition.y}px`,
        left: '0', right: '0', width: '100%', overflow: 'hidden',
        paddingRight: `${padding + scrollbar}px`,
      });
      this.querySelector('.pt-fit-guide__content').scrollTop = 0;
      this.querySelector('h2').focus({ preventScroll: true });
    }

    close() {
      this.dialog.close();
      // Native close events are queued; restore now so rapid reopen is safe.
      this.restore();
    }

    restore(returnFocus = true) {
      if (!this.previousStyle) return;
      Object.assign(document.body.style, this.previousStyle);
      this.previousStyle = null;
      window.scrollTo({ left: this.scrollPosition.x, top: this.scrollPosition.y, behavior: 'instant' });
      if (returnFocus && this.isConnected) {
        const target = this.returnFocus?.isConnected ? this.returnFocus : this.trigger;
        target.focus({ preventScroll: true });
      }
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.restore(false);
      this.events?.abort();
      this.events = null;
    }
  }

  class PtProductGuides extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const items = [...this.querySelectorAll('details')];
      // Native details[name] handles modern browsers. This also covers older Safari.
      for (const item of items) {
        item.addEventListener('toggle', () => {
          if (item.open) items.forEach((other) => { if (other !== item) other.open = false; });
        }, { signal: this.events.signal });
      }
    }
    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
    }
  }

  if (!customElements.get('pt-fit-guide')) customElements.define('pt-fit-guide', PtFitGuide);
  if (!customElements.get('pt-product-guides')) customElements.define('pt-product-guides', PtProductGuides);
})();
